import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { QrImage } from '../components/QrImage';

interface Entry { id: string; name: string; party_size: number; phone: string | null }
interface FreeTable { id: string; label: string }

function waLink(phone: string | null, text: string): string | null {
  if (!phone) return null;
  let digits = phone.replace(/\D/g, '');
  if (digits.length <= 11) digits = `55${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
}

export function WaitlistPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];

  const [entries, setEntries] = useState<Entry[]>([]);
  const [freeTables, setFreeTables] = useState<FreeTable[]>([]);
  const [pick, setPick] = useState<Record<string, string>>({});

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data } = await supabase
      .from('waitlist_entries')
      .select('id, name, party_size, phone')
      .eq('restaurant_id', restaurant.id)
      .eq('status', 'waiting')
      .order('created_at');
    if (data) setEntries(data as Entry[]);

    const { data: tbs } = await supabase.from('tables').select('id, label').eq('restaurant_id', restaurant.id).eq('is_active', true).eq('is_floating', false);
    const { data: open } = await supabase.from('table_sessions').select('table_id').eq('restaurant_id', restaurant.id).eq('status', 'open');
    const busy = new Set((open ?? []).map((o) => (o as { table_id: string }).table_id));
    setFreeTables(((tbs ?? []) as FreeTable[]).filter((tb) => !busy.has(tb.id)));
  }, [restaurant]);

  useEffect(() => {
    void reload();
    if (!restaurant) return;
    const ch = supabase
      .channel(`wl-${restaurant.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'waitlist_entries', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .on('postgres_changes', { event: '*', schema: 'public', table: 'table_sessions', filter: `restaurant_id=eq.${restaurant.id}` }, () => void reload())
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [restaurant, reload]);

  async function seat(entryId: string) {
    const tableId = pick[entryId];
    if (!tableId) return;
    await supabase.rpc('hostess_seat', { p_entry_id: entryId, p_table_id: tableId });
    void reload();
  }

  async function setStatus(id: string, status: 'cancelled' | 'no_show') {
    await supabase.from('waitlist_entries').update({ status }).eq('id', id);
    void reload();
  }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  const queueUrl = `${window.location.origin}/fila/${restaurant.slug}`;
  const name = restaurant.name;

  const buckets = [
    { key: 'group_2', match: (p: number) => p <= 2 },
    { key: 'group_4', match: (p: number) => p >= 3 && p <= 4 },
    { key: 'group_6', match: (p: number) => p >= 5 }
  ];

  const renderEntry = (e: Entry) => {
    const m1 = `Olá, ${e.name}! Sua mesa para ${e.party_size} no ${name} já está sendo preparada. Fique atento, em instantes chamaremos você!`;
    const m2 = `Olá, ${e.name}! Sua mesa no ${name} está pronta! 🎉 Temos uma tolerância de 10 minutos. Por favor, apresente-se à entrada!`;
    const l1 = waLink(e.phone, m1);
    const l2 = waLink(e.phone, m2);
    return (
      <div key={e.id} className="order-card">
        <div className="order-card__head">
          <strong>{e.name}</strong>
          <span className="tag">{e.party_size} {t('hostess.party')}</span>
        </div>
        <div className="order-card__actions" style={{ flexWrap: 'wrap', marginBottom: 10 }}>
          {l1 && <a className="btn btn--ghost btn--sm" href={l1} target="_blank" rel="noreferrer">{t('hostess.notify_soon')}</a>}
          {l2 && <a className="btn btn--ghost btn--sm" href={l2} target="_blank" rel="noreferrer">{t('hostess.notify_ready')}</a>}
        </div>
        <div className="form-row">
          <select value={pick[e.id] ?? ''} onChange={(ev) => setPick({ ...pick, [e.id]: ev.target.value })}>
            <option value="">{t('hostess.table')}…</option>
            {freeTables.map((tb) => (
              <option key={tb.id} value={tb.id}>{tb.label}</option>
            ))}
          </select>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => void seat(e.id)} disabled={!pick[e.id]}>
            {t('hostess.seat')}
          </button>
        </div>
        <div className="row-actions" style={{ marginTop: 10 }}>
          <button type="button" className="link-button" onClick={() => void setStatus(e.id, 'no_show')}>{t('hostess.no_show')}</button>
          <button type="button" className="link-button" onClick={() => void setStatus(e.id, 'cancelled')}>{t('hostess.cancel')}</button>
        </div>
      </div>
    );
  };

  return (
    <div className="page">
      <h1 className="page__title">{t('hostess.title')}</h1>

      <div className="qr-card" style={{ maxWidth: 220, marginBottom: 24 }}>
        <QrImage value={queueUrl} size={140} />
        <span className="muted-note" style={{ textAlign: 'center' }}>{t('hostess.qr_hint')}</span>
      </div>

      {entries.length === 0 ? (
        <p className="muted-note">{t('hostess.none')}</p>
      ) : (
        buckets.map((b) => {
          const list = entries.filter((e) => b.match(e.party_size));
          if (list.length === 0) return null;
          return (
            <div key={b.key}>
              <h2 className="section-title">{t(`hostess.${b.key}`)}</h2>
              <div className="order-grid">{list.map(renderEntry)}</div>
            </div>
          );
        })
      )}
    </div>
  );
}
